import { createTheme } from '@mui/material/styles';

// eGov palette from the «ЕСЕП · Reports list redesign» canvas (variant 1, MUI 7)
const navy = {
  main: '#083759',
  hover: '#1c4766',
  selected: '#305774',
  border: '#41657f',
  contrastText: '#ffffff',
  textSecondary: '#b5c3cd',
};

// Flat surfaces (elevation 1-3) carry a border, not a shadow; only floating layers cast one
const FLOAT = '0 4px 16px rgba(0,0,0,.12)';
const OVERLAY = '0 8px 32px rgba(0,0,0,.16)';
const shadows = ['none', 'none', 'none', 'none', ...Array(12).fill(FLOAT), ...Array(9).fill(OVERLAY)];

const theme = createTheme({
  // Exposes tokens as --mui-* so legacy CSS (Buttons.css, tabs) uses the same palette
  cssVariables: true,
  palette: {
    mode: 'light',
    primary: { main: '#2f57e4', dark: '#173fcc', light: '#ebf0fe', contrastText: '#fff' },
    navy,
    background: { default: '#f0f2f4', paper: '#fff' },
    divider: '#e6e9ec',
    text: { primary: 'rgba(0,0,0,.87)', secondary: '#546e7a' },
    // Tonal status chips: bg + fg pairs
    tonal: {
      brand: { bg: '#ebf0fe', fg: '#173fcc' },
      draft: { bg: '#fff', fg: '#424242', border: '#bdbdbd' },
      warning: { bg: '#fff4e5', fg: '#663c00' },
      purple: { bg: '#f8eff9', fg: '#4a2950' },
      info: { bg: '#e5f6fd', fg: '#014361' },
      success: { bg: '#edf7ed', fg: '#1e4620' },
    },
    table: { headBg: '#eceff1', headBorder: '#cfd8dc', headText: '#37474f', cellBorder: '#e0e0e0' },
    nav: { selectedBg: '#ebeff2', selectedHover: '#e1e7eb' },
    // Outlined field borders (MUI and legacy inputs)
    // Lighter than MUI's default .23/.87 — outlined controls should read as grey, not black
    field: { border: 'rgba(0,0,0,.14)', borderHover: 'rgba(0,0,0,.4)' },
  },
  shadows,
  shape: { borderRadius: 6 },
  typography: {
    fontFamily: 'Inter, "Segoe UI", Roboto, Arial, sans-serif',
    fontFamilyMono: '"Roboto Mono", ui-monospace, Consolas, monospace',
    h1: { fontSize: 24, lineHeight: '32px', fontWeight: 600 },
    h2: { fontSize: 20, lineHeight: '28px', fontWeight: 600 },
    h3: { fontSize: 16, lineHeight: '24px', fontWeight: 600 },
    body1: { fontSize: 14, lineHeight: '20px' },
    body2: { fontSize: 13, lineHeight: '20px' },
    caption: { fontSize: 12, lineHeight: '16px' },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, position: 'static' },
      styleOverrides: {
        root: { backgroundColor: navy.main, color: '#fff', borderBottom: `1px solid ${navy.main}` },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: { borderColor: '#e6e9ec', borderRadius: 8 },
        elevation1: { border: '1px solid #e6e9ec' },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.palette.field.border },
          '&:hover:not(.Mui-disabled, .Mui-error, .Mui-focused) .MuiOutlinedInput-notchedOutline': { borderColor: t.palette.field.borderHover },
          // 1px focus border instead of MUI's 2px
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1 },
        }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          position: 'static',
          padding: '16px 12px 4px',
          backgroundColor: 'transparent',
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 600,
          color: '#546e7a',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          minHeight: 36,
          padding: '4px 12px',
          borderRadius: 6,
          '& .MuiListItemIcon-root': { color: 'rgba(0,0,0,.54)' },
          '&.Mui-selected': {
            backgroundColor: t.palette.nav.selectedBg,
            color: navy.main,
            boxShadow: `inset 3px 0 0 ${navy.main}`,
            '& .MuiListItemText-primary': { fontWeight: 600 },
            '& .MuiListItemIcon-root': { color: navy.main },
            '&:hover': { backgroundColor: t.palette.nav.selectedHover },
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 32, '& .MuiSvgIcon-root': { fontSize: 20 } } },
    },
    MuiListItemText: {
      defaultProps: { slotProps: { primary: { variant: 'body1' } } },
      styleOverrides: { root: { margin: 0 } },
    },
    MuiTabs: {
      styleOverrides: { root: { minHeight: 48, borderBottom: '1px solid #e6e9ec' } },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          minWidth: 90,
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': { fontWeight: 600 },
          '&:hover': { backgroundColor: 'var(--mui-palette-action-hover)' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme: t }) => ({
          backgroundColor: t.palette.table.headBg,
          borderBottomColor: t.palette.table.headBorder,
          color: t.palette.table.headText,
          fontWeight: 600,
          fontSize: 13,
          whiteSpace: 'nowrap',
        }),
        body: ({ theme: t }) => ({ borderBottomColor: t.palette.table.cellBorder }),
        // Inner cells 10px, edges 16px so the table fits at 1366
        sizeSmall: {
          padding: '8px 10px',
          '&:first-of-type': { paddingLeft: 16 },
          '&:last-of-type': { paddingRight: 16 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 12, fontWeight: 500 },
        sizeSmall: { height: 24, fontSize: 13 },
      },
    },
    MuiPaginationItem: {
      styleOverrides: { root: { borderRadius: 6 } },
    },
    MuiBreadcrumbs: {
      styleOverrides: { root: { fontSize: 13 } },
    },
    MuiToggleButton: {
      styleOverrides: { root: { textTransform: 'none' } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 6 } },
    },
  },
});

export default theme;
